import React from 'react';
import { DollarSign, CreditCard, Calendar, User } from 'lucide-react';
import { useTranslation } from '../../../../contexts/LanguageContext';

const MobileTransactionList = ({ transactions, space, currentUserId, isAdmin }) => {
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMemberName = (memberId) => {
    const member = space.members.find(m => m.member_id === memberId);
    return member ? (member.name || member.member_email) : `Member ${memberId}`;
  };

  // Function to determine the display amount and color based on transaction type and user role
  const getTransactionDisplayInfo = (transaction) => {
    let displayAmount = transaction.amount;
    let displayColor = '';
    
    if (transaction.type === 'payment') {
      // For payment transactions:
      // - Admin sees negative (red)
      // - Non-admin sees positive (green)
      displayAmount = isAdmin ? -transaction.amount : transaction.amount;
      displayColor = displayAmount > 0 ? 'text-green-600' : 'text-red-600';
    } else if (transaction.type === 'subscription_charge') {
      // For automatic transactions:
      // - Admin sees positive (green)
      // - Non-admin sees negative (red)
      displayAmount = isAdmin ? transaction.amount : -transaction.amount;
      displayColor = displayAmount > 0 ? 'text-green-600' : 'text-red-600';
    } else {
      // For other transaction types, use default logic
      const isPayment = transaction.amount > 0;
      displayColor = isPayment ? 'text-green-600' : 'text-red-600';
    }
    
    return { displayAmount, displayColor };
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="font-semibold mb-3 flex items-center">
        <CreditCard className="h-5 w-5 mr-2" />
        {t('sharesubs.transactionHistory.title')}
      </h4>
      
      {transactions.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          {t('sharesubs.transactionHistory.noTransactions')}
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {transactions.map((transaction, index) => {
            const { displayAmount, displayColor } = getTransactionDisplayInfo(transaction);
            const isCurrentUser = transaction.member_id === currentUserId;
            
            return (
              <div key={index} className="bg-white p-3 rounded-lg border">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium">{transaction.description}</div>
                    
                    {(isAdmin || isCurrentUser) && (
                      <div className="text-sm text-gray-500 mt-1 flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        {getMemberName(transaction.member_id)}
                        {isCurrentUser && ` (${t('sharesubs.members.you')})`}
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 mt-1 flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDate(transaction.created_at)}
                    </div>
                  </div>
                  
                  <div className={`text-right font-medium ${displayColor}`}>
                    {displayAmount > 0 ? '+' : ''}{displayAmount.toFixed(2)}€
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 mt-2">
                  {t('sharesubs.transactionHistory.type')}: {transaction.type}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileTransactionList;